/**
 * An organization's GitHub installation (§5.16, §5.25): Airlock's own GitHub
 * App, installed on the customer's GitHub organization and linked to an Airlock
 * organization, so that its members' machines open pull requests, push and
 * answer reviews with a short-lived token the account Worker mints for one
 * workspace's repositories — instead of each person's own sign-in.
 *
 * **One app, Airlock's.** It is registered once by Airlock and configured on
 * the Worker by environment (`GITHUB_APP_ID`, `GITHUB_APP_SLUG`, the private key
 * as a secret); a customer installs it on their GitHub organization from its
 * public page. The alternative — a GitHub App created per customer through the
 * manifest flow — would hand the Worker one private key per organization to
 * keep, and a customer one more thing to administer, for nothing a scoped
 * installation token does not already give.
 *
 * **Never stored on a machine.** A token answers `expiresAt` (an hour, GitHub's
 * own lifetime) and the app holds it in memory until a few minutes before then;
 * it reaches host `gh` and host git only (0231/token-never-in-sandbox), exactly
 * as a person's own sign-in does.
 */

/** `GET /api/orgs/:id/github` — what the organization's GitHub page shows. Never a token. */
export interface OrgGithubState {
  /** Whether this deployment has an app to install at all (its id, slug and key are set). */
  configured: boolean;
  /** The app's public page, where it is installed — null when not configured. */
  appUrl: string | null;
  link: OrgGithubLink | null;
}

export interface OrgGithubLink {
  installationId: number;
  /** The GitHub account it is installed on — `acme`, an organization or a user. */
  account: string;
  accountType: 'Organization' | 'User';
  linkedAt: string;
  linkedBy: { id: string | null; label: string };
}

/** `POST /api/orgs/:id/github/connect` — where to send the browser to install it. */
export interface OrgGithubConnect {
  url: string;
}

/**
 * `POST /api/cloud/orgs/:id/github/token` with `{ workspaceId }` — a token for
 * that workspace's repositories on the installation, or why there is none.
 * `token: null` is an ordinary answer (nothing configured, nothing linked, no
 * repository of the workspace approved by an administrator — `OrgGithubRepos` —
 * or none on the installation): the app falls back to the
 * person's own sign-in and says nothing.
 */
export type GithubTokenGrant =
  | {
      token: string;
      /** ISO instant GitHub gave; the app forgets the token before it. */
      expiresAt: string;
      /** `owner/name`, lower-case, each repository the token reaches. */
      repositories: string[];
    }
  | { token: null; reason: string };

/** What a token minted for a workspace may do — and nothing an app could not do from a person's checkout. */
export const GITHUB_TOKEN_PERMISSIONS = {
  contents: 'write',
  pull_requests: 'write',
  metadata: 'read',
  checks: 'read'
} as const;

/**
 * The `owner/name` of each GitHub repository of a workspace, read off the
 * identities its document carries (`remote.ts`: `github.com/acme/api`). A
 * monorepo's folders share their repository's identity, so each one is named
 * once.
 */
export function githubRepositoriesOf(remotes: readonly string[]): string[] {
  const out = new Set<string>();
  for (const remote of remotes) {
    const match = /^github\.com\/([A-Za-z0-9_.-]+)\/([A-Za-z0-9_.-]+)$/.exec(remote);
    if (match) out.add(`${match[1]}/${match[2]}`.toLowerCase());
  }
  return [...out].sort();
}

/**
 * The repositories an owner or an administrator approved for the
 * organization's installation (security review SEC-04, 2026-10-24). A token is
 * minted only over a workspace's repositories that are on this list *and* that
 * the installation reaches: a workspace's document is written by whoever has
 * `write` on it, and any seat holder can create one, so the document alone
 * never decides which repositories a token pushes to. The rules repository a
 * workspace names is held to the same list. An organization starts with none.
 *
 * `GET /api/orgs/:id/github/repos` — every member reads it; `canEdit` for an
 * owner or an admin of an organization that is not deactivated, who adds with
 * `POST …/repos {repo}` and removes with `DELETE …/repos/:owner/:name`.
 */
export interface OrgGithubRepos {
  repositories: OrgGithubRepo[];
  canEdit: boolean;
}

export interface OrgGithubRepo {
  /** `owner/name`, lower-case. */
  repo: string;
  addedAt: string;
  addedBy: { id: string | null; label: string };
}

/** `GET /api/orgs/:id/github/repos/reachable` — what the installation reaches, offered to an administrator; null when nothing is linked. */
export interface OrgGithubReachable {
  repositories: string[] | null;
}

/** How many repositories one organization may approve. */
export const MAX_APPROVED_GITHUB_REPOS = 500;

/**
 * `owner/name`, lower-case, out of what an administrator typed — the name
 * alone, `github.com/owner/name`, or a clone address (`https://…`, `.git`,
 * `git@github.com:owner/name`); null for anything else.
 */
export function githubRepoName(text: string): string | null {
  const trimmed = text
    .trim()
    .replace(/^git@github\.com:/i, '')
    .replace(/^(?:https?:\/\/)?(?:www\.)?github\.com\//i, '')
    .replace(/\.git$/i, '')
    .replace(/\/+$/, '');
  const match = /^([A-Za-z0-9_.-]{1,100})\/([A-Za-z0-9_.-]{1,100})$/.exec(trimmed);
  if (!match || match[1].startsWith('.') || match[2] === '.' || match[2] === '..') return null;
  return `${match[1]}/${match[2]}`.toLowerCase();
}
